import { createContext, useCallback, useContext, useEffect } from "react";
import {
  useAuth as useClerkAuth,
  useSession as useClerkSession,
} from "@clerk/clerk-react";
import { useMemo } from "react";

interface AuthContext {
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
  getHeaders: () => Promise<Record<string, string>>;
}

export const AuthContext = createContext<AuthContext | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const { session } = useClerkSession();

  //respond to changes in clerk state
  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      return;
    }
    const email = session?.user.primaryEmailAddress?.emailAddress;
    if (
      email &&
      !(
        email.endsWith("@joinliora.com") ||
        email.endsWith("@ems-inc.ca") ||
        email.includes("+")
      )
    ) {
      //Redirect external users to the client dashboard
      window.location.href = "https://app.joinliora.com/";
      return;
    }
  }, [isLoaded, isSignedIn, session]);

  const _getToken = useCallback(async () => {
    return await getToken({ template: "ems" });
  }, [getToken]);

  const getHeaders = useCallback(async (): Promise<Record<string, string>> => {
    return {
      Authorization: "Bearer " + (await _getToken()),
      "Content-Type": "application/json",
    };
  }, [_getToken]);
  const authValue = useMemo(
    () => ({
      signOut,
      getToken: _getToken,
      getHeaders,
      session
    }),
    [isSignedIn, signOut, getToken, getHeaders],
  );
  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
